import React, { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { PageHeader, EmptyState, StatusBadge } from "../components/common";
import { Button } from "../components/ui/button";
import { toast } from "sonner";
import {
  Video, Camera, Upload, Loader2, ShieldCheck, MapPin, Hash, Mic,
  Sparkles, AlertTriangle, FileSignature, Trash2, Scale, ArrowRight,
  CheckCircle2, Square, Circle, RefreshCw, Lock, Eye,
} from "lucide-react";

const VIDEO_MAX_SECONDS = 60;
const BURST_MAX = 8;
const JURISDICTIONS = [
  { code: "IN", label: "India · FIR (BNSS s.173 / CrPC s.154)" },
  { code: "US", label: "United States · Citizen Police Report" },
  { code: "EU", label: "European Union · Police Complaint" },
];

function urgencyTone(u) {
  if (u === "high") return "danger";
  if (u === "medium") return "warn";
  return "neutral";
}

export default function IncidentCapturePage() {
  const navigate = useNavigate();
  const [addonEnabled, setAddonEnabled] = useState(null);
  const [quota, setQuota] = useState(null);
  const [incidents, setIncidents] = useState([]);
  const [hasMore, setHasMore] = useState(false);

  // Capture state
  const [mode, setMode] = useState("video"); // "video" | "burst"
  const [recording, setRecording] = useState(false);
  const [elapsed, setElapsed] = useState(0);
  const [stream, setStream] = useState(null);
  const [pendingFile, setPendingFile] = useState(null); // {file, previewUrl} for video
  const [burstFiles, setBurstFiles] = useState([]); // [{file, previewUrl}]

  // Privacy options
  const [faceBlur, setFaceBlur] = useState(true);
  const [stripGps, setStripGps] = useState(true);
  const [gps, setGps] = useState(null);

  // Submission state
  const [uploading, setUploading] = useState(false);
  const [analyzing, setAnalyzing] = useState(null);
  const [drafting, setDrafting] = useState(null);
  const [lastIncident, setLastIncident] = useState(null);
  const [jurisdiction, setJurisdiction] = useState("IN");

  const videoRef = useRef(null);
  const recorderRef = useRef(null);
  const chunksRef = useRef([]);
  const timerRef = useRef(null);
  const videoFileInputRef = useRef(null);
  const burstFileInputRef = useRef(null);

  const PAGE = 20;

  const refresh = async () => {
    try {
      const [statusRes, q] = await Promise.all([
        api.get("/nyaya/status").then((r) => r.data),
        api.get("/incidents/quota").then((r) => r.data).catch(() => null),
      ]);
      setAddonEnabled(!!statusRes.enabled);
      setQuota(q);
      if (statusRes.enabled) {
        const { data } = await api.get("/incidents", { params: { limit: PAGE } });
        setIncidents(data);
        setHasMore(data.length === PAGE);
      } else {
        setIncidents([]);
      }
    } catch {
      setAddonEnabled(false);
    }
  };
  useEffect(() => { refresh(); return () => stopStream(); /* eslint-disable-next-line */ }, []);

  const enableAddon = async () => {
    await api.post("/nyaya/enable");
    toast.success("Nyaya Drishti add-on enabled — Sentinel unlocked.");
    refresh();
  };

  // ----- GPS -----
  const captureGPS = () => {
    if (!navigator.geolocation) {
      toast.error("This browser does not support geolocation.");
      return;
    }
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setGps({ lat: pos.coords.latitude, lng: pos.coords.longitude });
        toast.success(`Location captured (${pos.coords.latitude.toFixed(4)}, ${pos.coords.longitude.toFixed(4)})`);
      },
      () => toast.error("Location permission denied."),
      { enableHighAccuracy: true, timeout: 10000 },
    );
  };

  // ----- Camera (live) -----
  const startCamera = async () => {
    try {
      const s = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: "environment" }, audio: mode === "video",
      });
      setStream(s);
      if (videoRef.current) videoRef.current.srcObject = s;
    } catch {
      toast.error("Camera permission denied or unavailable.");
    }
  };

  const stopStream = () => {
    if (stream) stream.getTracks().forEach((t) => t.stop());
    setStream(null);
    if (videoRef.current) videoRef.current.srcObject = null;
  };

  // ----- Video record -----
  const startRecording = () => {
    if (!stream) return;
    chunksRef.current = [];
    const mimeType = MediaRecorder.isTypeSupported("video/webm;codecs=vp9,opus")
      ? "video/webm;codecs=vp9,opus"
      : MediaRecorder.isTypeSupported("video/webm;codecs=vp8,opus")
        ? "video/webm;codecs=vp8,opus"
        : "video/webm";
    const rec = new MediaRecorder(stream, { mimeType });
    rec.ondataavailable = (e) => { if (e.data.size > 0) chunksRef.current.push(e.data); };
    rec.onstop = () => {
      const blob = new Blob(chunksRef.current, { type: "video/webm" });
      const file = new File([blob], `incident-${Date.now()}.webm`, { type: "video/webm" });
      setPendingFile({ file, previewUrl: URL.createObjectURL(blob) });
      stopStream();
    };
    rec.start();
    recorderRef.current = rec;
    setRecording(true);
    setElapsed(0);
    timerRef.current = setInterval(() => {
      setElapsed((s) => {
        const next = s + 1;
        if (next >= VIDEO_MAX_SECONDS) stopRecording();
        return next;
      });
    }, 1000);
  };

  const stopRecording = () => {
    if (recorderRef.current && recorderRef.current.state !== "inactive") {
      recorderRef.current.stop();
    }
    clearInterval(timerRef.current);
    setRecording(false);
  };

  // ----- Photo burst -----
  const captureBurstShot = async () => {
    if (!stream || !videoRef.current) return;
    const v = videoRef.current;
    const canvas = document.createElement("canvas");
    canvas.width = v.videoWidth || 1280;
    canvas.height = v.videoHeight || 720;
    canvas.getContext("2d").drawImage(v, 0, 0);
    canvas.toBlob((blob) => {
      const file = new File([blob], `burst-${burstFiles.length + 1}-${Date.now()}.jpg`, { type: "image/jpeg" });
      setBurstFiles((prev) => [...prev, { file, previewUrl: URL.createObjectURL(blob) }].slice(0, BURST_MAX));
    }, "image/jpeg", 0.9);
  };

  const clearBurst = () => {
    burstFiles.forEach((b) => URL.revokeObjectURL(b.previewUrl));
    setBurstFiles([]);
  };

  // ----- File pickers (upload pre-recorded) -----
  const onVideoPicked = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setPendingFile({ file: f, previewUrl: URL.createObjectURL(f) });
  };

  const onBurstPicked = (e) => {
    const list = Array.from(e.target.files || []).slice(0, BURST_MAX);
    if (list.length < 2) { toast.error("Pick at least 2 images for a burst."); return; }
    setBurstFiles(list.map((f) => ({ file: f, previewUrl: URL.createObjectURL(f) })));
  };

  // ----- Submit -----
  const submitVideo = async () => {
    if (!pendingFile) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", pendingFile.file);
      fd.append("captured_at", new Date().toISOString());
      if (gps) {
        fd.append("gps_lat", String(gps.lat));
        fd.append("gps_lng", String(gps.lng));
      }
      fd.append("privacy_face_blur", String(faceBlur));
      fd.append("privacy_strip_gps", String(stripGps));
      const { data } = await api.post("/incidents/upload-video", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("Evidence sealed. SHA-256 chain captured.");
      setPendingFile(null);
      setLastIncident(data);
      runAnalyze(data.id);
      refresh();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const submitBurst = async () => {
    if (burstFiles.length < 2) return;
    setUploading(true);
    try {
      const fd = new FormData();
      burstFiles.forEach((b) => fd.append("files", b.file));
      fd.append("captured_at", new Date().toISOString());
      if (gps) {
        fd.append("gps_lat", String(gps.lat));
        fd.append("gps_lng", String(gps.lng));
      }
      fd.append("privacy_face_blur", String(faceBlur));
      fd.append("privacy_strip_gps", String(stripGps));
      const { data } = await api.post("/incidents/upload-burst", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("Burst sealed. SHA-256 chain captured.");
      clearBurst();
      setLastIncident(data);
      runAnalyze(data.id);
      refresh();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const runAnalyze = async (id) => {
    setAnalyzing(id);
    try {
      const { data } = await api.post(`/incidents/${id}/analyze`);
      setLastIncident(data);
      refresh();
      toast.success("AI analysis complete.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Analysis failed");
    } finally {
      setAnalyzing(null);
    }
  };

  const draftFIR = async (id) => {
    setDrafting(id);
    try {
      const { data } = await api.post("/incidents/draft-fir", {
        incident_id: id, jurisdiction,
      });
      toast.success(`Draft prepared: ${data.title}`);
      navigate(`/app/drafts/${data.draft_id}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Draft failed");
    } finally {
      setDrafting(null);
    }
  };

  const createCase = async (id) => {
    try {
      const { data } = await api.post("/incidents/create-case", { incident_id: id });
      toast.success(data.already_linked ? "Case already linked." : "Case created from incident.");
      navigate(`/app/cases/${data.case_id}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not create case");
    }
  };

  const deleteIncident = async (id) => {
    if (!window.confirm("Delete this incident record and its keyframes?")) return;
    try {
      await api.delete(`/incidents/${id}`);
      toast.message("Incident deleted.");
      if (lastIncident?.id === id) setLastIncident(null);
      refresh();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not delete");
    }
  };

  if (addonEnabled === null) return <div className="label-eyebrow">Loading…</div>;

  // Locked / add-on disabled screen
  if (!addonEnabled) {
    return (
      <div data-testid="sentinel-locked">
        <PageHeader
          eyebrow="Nyaya Drishti · Sentinel"
          title="Capture an incident as it happens."
          description="Record a short video clip or a burst of photos. Marianne hashes the evidence, transcribes the audio, analyses the scene with Claude vision, and drafts a jurisdiction-aware FIR or police complaint — all from inside the same Nyaya Drishti add-on."
        />
        <div className="grid lg:grid-cols-5 gap-6">
          <div className="lg:col-span-3 border border-[#E8E3F0] bg-white p-8">
            <Video size={28} strokeWidth={1.4} className="text-[#7B56C9] mb-4" />
            <h3 className="font-serif text-3xl mb-4">What Sentinel does</h3>
            <ul className="space-y-3 text-sm">
              {[
                "In-browser video recording (≤60 s) OR pre-recorded upload",
                "Photo-burst mode (2–8 stills) for when bandwidth is poor",
                "SHA-256 evidence hash — tamper-evident chain of custody",
                "Audio transcription via OpenAI Whisper",
                "Scene analysis via Claude vision — offence categories, IPC/BNS hints, urgency",
                "Face-blur on stored copies (toggle), GPS-strip from file metadata (toggle)",
                "One-click FIR / Police Complaint draft for India · US · EU",
              ].map((f) => (
                <li key={f} className="flex items-start gap-2.5">
                  <CheckCircle2 size={16} className="text-[#7B56C9] mt-0.5 shrink-0" />
                  <span>{f}</span>
                </li>
              ))}
            </ul>
          </div>
          <div className="lg:col-span-2 border border-[#7B56C9] bg-[#FAFAF8] p-8 flex flex-col">
            <div className="label-eyebrow text-[#C5A059] mb-3">Included in Nyaya Drishti</div>
            <div className="flex items-baseline gap-2 mb-3">
              <div className="font-serif text-6xl tracking-tight">₹1,200</div>
              <div className="text-sm text-[#4A5568]">/ month</div>
            </div>
            <div className="text-sm text-[#2A0F5A]/75 mb-6 leading-relaxed">
              Sentinel is part of the same Nyaya Drishti add-on — no extra fee. Filings + incidents, one toggle.
            </div>
            <Button onClick={enableAddon} className="rounded-none bg-[#2A0F5A] hover:bg-[#3D1F85] text-[#FAFAF8] mb-3" data-testid="enable-sentinel-btn">
              <Sparkles size={14} className="mr-1.5" /> Enable Nyaya Drishti
            </Button>
            <div className="flex items-center gap-1.5 text-[11px] text-[#4A5568]">
              <Lock size={12} className="text-[#7B56C9]" /> Toggle off any time from Settings.
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Active workspace
  return (
    <div data-testid="sentinel-workspace">
      <PageHeader
        eyebrow="Nyaya Drishti · Sentinel"
        title="Citizen Incident Capture"
        description="Record or upload. Marianne seals it with a SHA-256 hash, transcribes it, watches it with Claude, and drafts your FIR."
        action={
          <div className="flex items-center gap-2">
            {quota && (
              <div className="hidden sm:flex items-center gap-1.5 text-xs px-2.5 py-1 border border-[#E8E3F0] bg-white" data-testid="sentinel-quota">
                <span className="label-eyebrow !text-[10px] text-[#C5A059]">Today</span>
                <span className="font-mono">{quota.used_today}/{quota.limit}</span>
              </div>
            )}
            <Link to="/app/nyaya" className="text-xs border border-[#E8E3F0] px-3 py-1.5 hover:bg-[#FAFAF8]" data-testid="open-nyaya-from-sentinel">
              <Eye size={12} className="inline mr-1" /> Filings (Nyaya)
            </Link>
            <button onClick={refresh} className="p-2 hover:bg-[#FAFAF8] border border-[#E8E3F0]" title="Refresh" data-testid="sentinel-refresh-btn"><RefreshCw size={14} /></button>
          </div>
        }
      />

      {/* Mode tabs */}
      <div className="mb-4 inline-flex border border-[#E8E3F0] bg-white">
        {[
          { id: "video", label: "Video (≤60s)", icon: Video },
          { id: "burst", label: "Photo burst", icon: Camera },
        ].map((m) => {
          const Icon = m.icon;
          const active = mode === m.id;
          return (
            <button
              key={m.id}
              onClick={() => { stopStream(); stopRecording(); setMode(m.id); }}
              className={`px-4 py-2 text-sm flex items-center gap-2 ${active ? "bg-[#2A0F5A] text-[#FAFAF8]" : "text-[#2A0F5A] hover:bg-[#FAFAF8]"}`}
              data-testid={`mode-${m.id}-btn`}
            >
              <Icon size={14} /> {m.label}
            </button>
          );
        })}
      </div>

      {/* Privacy strip */}
      <div className="flex flex-wrap items-center gap-4 mb-6 text-xs text-[#2A0F5A]/75" data-testid="privacy-strip">
        <label className="flex items-center gap-1.5 cursor-pointer">
          <input type="checkbox" checked={faceBlur} onChange={(e) => setFaceBlur(e.target.checked)} data-testid="toggle-face-blur" />
          <ShieldCheck size={12} className="text-[#7B56C9]" /> Blur faces on stored keyframes
        </label>
        <label className="flex items-center gap-1.5 cursor-pointer">
          <input type="checkbox" checked={stripGps} onChange={(e) => setStripGps(e.target.checked)} data-testid="toggle-strip-gps" />
          <MapPin size={12} className="text-[#7B56C9]" /> Strip GPS from stored file
        </label>
        <button
          onClick={captureGPS}
          className={`flex items-center gap-1.5 px-2.5 py-1 border ${gps ? "border-[#7B56C9] text-[#7B56C9]" : "border-[#E8E3F0]"}`}
          data-testid="capture-gps-btn"
        >
          <MapPin size={12} /> {gps ? `${gps.lat.toFixed(3)}, ${gps.lng.toFixed(3)}` : "Tag this location"}
        </button>
        <span className="text-[11px] text-[#4A5568]">GPS, if tagged, is kept on the case record only — never in the file.</span>
      </div>

      {/* Capture pane */}
      {mode === "video" && (
        <div className="border border-[#E8E3F0] bg-white p-6 mb-6">
          <div className="label-eyebrow mb-3">Video evidence</div>
          {!stream && !pendingFile && (
            <div className="flex flex-wrap items-center gap-3">
              <Button onClick={startCamera} className="rounded-none bg-[#2A0F5A] hover:bg-[#3D1F85] text-[#FAFAF8]" data-testid="start-camera-btn">
                <Circle size={12} className="mr-1.5 fill-red-500 text-red-500" /> Open camera
              </Button>
              <label>
                <input ref={videoFileInputRef} type="file" accept="video/*" className="hidden" onChange={onVideoPicked} data-testid="video-file-input" />
                <span className="inline-flex items-center gap-2 px-4 py-2 cursor-pointer text-sm border border-[#E8E3F0] hover:bg-[#FAFAF8]">
                  <Upload size={14} /> Upload pre-recorded
                </span>
              </label>
            </div>
          )}
          {stream && !pendingFile && (
            <div className="space-y-3">
              <video ref={videoRef} autoPlay playsInline muted className="w-full max-w-md border border-[#E8E3F0] bg-black" data-testid="sentinel-video" />
              <div className="flex items-center gap-3">
                {!recording ? (
                  <Button onClick={startRecording} className="rounded-none bg-red-700 hover:bg-red-800 text-white" data-testid="record-start-btn">
                    <Circle size={12} className="mr-1.5 fill-white" /> Start recording
                  </Button>
                ) : (
                  <Button onClick={stopRecording} className="rounded-none bg-[#2A0F5A] hover:bg-[#3D1F85] text-white" data-testid="record-stop-btn">
                    <Square size={12} className="mr-1.5 fill-white" /> Stop ({elapsed}s)
                  </Button>
                )}
                <Button variant="outline" className="rounded-none border-[#E8E3F0]" onClick={stopStream}>Cancel</Button>
                {recording && <span className="text-xs text-red-700 animate-pulse">● REC · {elapsed}s / {VIDEO_MAX_SECONDS}s</span>}
              </div>
            </div>
          )}
          {pendingFile && (
            <div className="space-y-3">
              <video src={pendingFile.previewUrl} controls className="w-full max-w-md border border-[#E8E3F0] bg-black" data-testid="sentinel-video-preview" />
              <div className="text-xs text-[#4A5568]">{pendingFile.file.name} · {(pendingFile.file.size / 1024 / 1024).toFixed(2)} MB</div>
              <div className="flex gap-2">
                <Button onClick={submitVideo} disabled={uploading} className="rounded-none bg-[#2A0F5A] hover:bg-[#3D1F85] text-[#FAFAF8]" data-testid="submit-video-btn">
                  {uploading ? <><Loader2 size={14} className="mr-1.5 animate-spin" /> Sealing…</> : <><ShieldCheck size={14} className="mr-1.5" /> Seal evidence & analyse</>}
                </Button>
                <Button variant="outline" className="rounded-none border-[#E8E3F0]" onClick={() => { URL.revokeObjectURL(pendingFile.previewUrl); setPendingFile(null); }}>
                  Discard
                </Button>
              </div>
            </div>
          )}
        </div>
      )}

      {mode === "burst" && (
        <div className="border border-[#E8E3F0] bg-white p-6 mb-6">
          <div className="label-eyebrow mb-3">Photo burst evidence ({burstFiles.length}/{BURST_MAX})</div>
          {!stream && burstFiles.length === 0 && (
            <div className="flex flex-wrap items-center gap-3">
              <Button onClick={startCamera} className="rounded-none bg-[#2A0F5A] hover:bg-[#3D1F85] text-[#FAFAF8]" data-testid="burst-camera-btn">
                <Camera size={14} className="mr-1.5" /> Use camera
              </Button>
              <label>
                <input ref={burstFileInputRef} type="file" accept="image/*" multiple className="hidden" onChange={onBurstPicked} data-testid="burst-file-input" />
                <span className="inline-flex items-center gap-2 px-4 py-2 cursor-pointer text-sm border border-[#E8E3F0] hover:bg-[#FAFAF8]">
                  <Upload size={14} /> Pick {`>=2`} images
                </span>
              </label>
            </div>
          )}
          {stream && (
            <div className="space-y-3">
              <video ref={videoRef} autoPlay playsInline muted className="w-full max-w-md border border-[#E8E3F0] bg-black" data-testid="sentinel-burst-video" />
              <div className="flex gap-2">
                <Button onClick={captureBurstShot} disabled={burstFiles.length >= BURST_MAX} className="rounded-none bg-[#7B56C9] hover:bg-[#6844BA] text-white" data-testid="burst-snap-btn">
                  <Camera size={14} className="mr-1.5" /> Snap ({burstFiles.length}/{BURST_MAX})
                </Button>
                <Button variant="outline" className="rounded-none border-[#E8E3F0]" onClick={stopStream}>Done capturing</Button>
              </div>
            </div>
          )}
          {burstFiles.length > 0 && (
            <div className="mt-4 space-y-3">
              <div className="grid grid-cols-4 sm:grid-cols-8 gap-2">
                {burstFiles.map((b, i) => (
                  <img key={i} src={b.previewUrl} alt={`shot ${i + 1}`} className="aspect-square object-cover border border-[#E8E3F0]" />
                ))}
              </div>
              <div className="flex gap-2">
                <Button
                  onClick={submitBurst}
                  disabled={uploading || burstFiles.length < 2}
                  className="rounded-none bg-[#2A0F5A] hover:bg-[#3D1F85] text-[#FAFAF8]"
                  data-testid="submit-burst-btn"
                >
                  {uploading ? <><Loader2 size={14} className="mr-1.5 animate-spin" /> Sealing…</> : <><ShieldCheck size={14} className="mr-1.5" /> Seal {burstFiles.length} frames & analyse</>}
                </Button>
                <Button variant="outline" className="rounded-none border-[#E8E3F0]" onClick={clearBurst}>Clear</Button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Last incident result */}
      {lastIncident && (
        <div className="border border-[#7B56C9] bg-white p-6 mb-8" data-testid="sentinel-last-incident">
          <div className="flex items-start justify-between gap-4 flex-wrap mb-4">
            <div>
              <div className="label-eyebrow text-[#C5A059] mb-1">
                {lastIncident.mode === "video" ? "Video evidence" : "Photo-burst evidence"} ·{" "}
                {new Date(lastIncident.created_at).toLocaleString()}
              </div>
              <h3 className="font-serif text-3xl">
                {analyzing === lastIncident.id ? (
                  <span className="text-[#7B56C9] flex items-center gap-2 text-2xl"><Loader2 size={20} className="animate-spin" /> Marianne is observing…</span>
                ) : lastIncident.scene_description || "Awaiting analysis"}
              </h3>
            </div>
            <div className="flex flex-wrap gap-2">
              {lastIncident.urgency && lastIncident.urgency !== "low" && (
                <StatusBadge tone={urgencyTone(lastIncident.urgency)}>urgency · {lastIncident.urgency}</StatusBadge>
              )}
              {(lastIncident.offence_categories || []).length > 0 && (
                <StatusBadge tone="warn">{lastIncident.offence_categories.length} offence{lastIncident.offence_categories.length > 1 ? "s" : ""}</StatusBadge>
              )}
              {lastIncident.storage_unavailable && <StatusBadge tone="warn">storage offline</StatusBadge>}
            </div>
          </div>

          <dl className="grid md:grid-cols-2 gap-x-8 gap-y-3 text-sm mb-4">
            <Field label="Evidence SHA-256">
              <span className="font-mono text-[11px] break-all flex items-center gap-1.5">
                <Hash size={11} className="text-[#7B56C9] shrink-0" /> {lastIncident.evidence_hash || "—"}
              </span>
            </Field>
            <Field label="Captured at" value={lastIncident.captured_at} />
            <Field label="GPS (record)" value={lastIncident.gps_lat ? `${lastIncident.gps_lat.toFixed(5)}, ${lastIncident.gps_lng.toFixed(5)}` : ""} />
            <Field label="Privacy" value={`${lastIncident.privacy_face_blur ? "face-blur on" : "face-blur off"} · ${lastIncident.privacy_strip_gps ? "gps-strip on" : "gps-strip off"}`} />
            <Field label="Offence categories" value={(lastIncident.offence_categories || []).join(", ")} />
            <Field label="Statute hints" value={(lastIncident.ipc_bnss_hints || []).join(", ")} />
          </dl>

          {lastIncident.transcript && (
            <div className="border-l-2 border-[#7B56C9] bg-[#7B56C9]/5 px-3 py-2 mb-4 text-sm" data-testid="sentinel-transcript">
              <div className="font-medium flex items-center gap-1.5 mb-1 text-[#7B56C9]">
                <Mic size={14} /> Transcript {lastIncident.language_detected ? `· ${lastIncident.language_detected}` : ""}
              </div>
              <pre className="text-xs whitespace-pre-wrap font-mono max-h-40 overflow-auto">{lastIncident.transcript}</pre>
            </div>
          )}

          {(lastIncident.visible_weapons_or_objects || []).length > 0 && (
            <div className="border-l-2 border-[#C5A059] bg-[#C5A059]/10 px-3 py-2 mb-4 text-sm">
              <div className="font-medium flex items-center gap-1.5 mb-1"><AlertTriangle size={14} className="text-[#8a6b1f]" /> Objects observed</div>
              <ul className="text-xs text-[#8a6b1f] list-disc pl-5">
                {lastIncident.visible_weapons_or_objects.map((o, i) => <li key={i}>{o}</li>)}
              </ul>
            </div>
          )}

          <div className="flex flex-wrap items-center gap-2">
            <select
              value={jurisdiction}
              onChange={(e) => setJurisdiction(e.target.value)}
              className="border border-[#E8E3F0] bg-white px-3 py-2 text-sm rounded-none"
              data-testid="jurisdiction-select"
            >
              {JURISDICTIONS.map((j) => <option key={j.code} value={j.code}>{j.label}</option>)}
            </select>
            <Button
              onClick={() => draftFIR(lastIncident.id)}
              disabled={drafting === lastIncident.id || !lastIncident.analyzed}
              className="rounded-none bg-[#2A0F5A] hover:bg-[#3D1F85] text-[#FAFAF8]"
              data-testid="draft-fir-btn"
            >
              {drafting === lastIncident.id
                ? <><Loader2 size={14} className="mr-1.5 animate-spin" /> Drafting…</>
                : <><FileSignature size={14} className="mr-1.5" /> Draft FIR / Complaint <ArrowRight size={14} className="ml-1.5" /></>}
            </Button>
            <Button
              onClick={() => createCase(lastIncident.id)}
              variant="outline"
              className="rounded-none border-[#E8E3F0]"
              data-testid="create-case-from-incident-btn"
            >
              <Scale size={14} className="mr-1.5" /> Create case
            </Button>
            {!lastIncident.analyzed && (
              <Button onClick={() => runAnalyze(lastIncident.id)} variant="outline" className="rounded-none border-[#E8E3F0]" data-testid="reanalyze-btn">
                <Sparkles size={14} className="mr-1.5" /> Run analysis
              </Button>
            )}
          </div>
        </div>
      )}

      {/* Recent incidents */}
      <div>
        <div className="label-eyebrow mb-3">Recent incidents</div>
        {incidents.length === 0 ? (
          <EmptyState icon={Video} title="No incidents captured yet" description="Record a clip, snap a burst, or upload a pre-recorded file." />
        ) : (
          <div className="border border-[#E8E3F0] bg-white">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-[#E8E3F0]">
                  <th className="text-left px-4 py-3 label-eyebrow">When</th>
                  <th className="text-left px-4 py-3 label-eyebrow">Mode</th>
                  <th className="text-left px-4 py-3 label-eyebrow hidden md:table-cell">Scene</th>
                  <th className="text-left px-4 py-3 label-eyebrow">Urgency</th>
                  <th className="text-right px-4 py-3"></th>
                </tr>
              </thead>
              <tbody>
                {incidents.map((i) => (
                  <tr key={i.id} className="border-b border-[#E8E3F0] hover:bg-[#FAFAF8]" data-testid={`incident-row-${i.id}`}>
                    <td className="px-4 py-3 text-xs text-[#4A5568]">{new Date(i.created_at).toLocaleString()}</td>
                    <td className="px-4 py-3 capitalize">{i.mode.replace("_", " ")}</td>
                    <td className="px-4 py-3 hidden md:table-cell text-xs truncate max-w-md">{i.scene_description || (i.analyzed ? "—" : "Not analysed")}</td>
                    <td className="px-4 py-3">
                      {i.urgency && i.urgency !== "low"
                        ? <StatusBadge tone={urgencyTone(i.urgency)}>{i.urgency}</StatusBadge>
                        : <span className="text-xs text-[#4A5568]">{i.analyzed ? "low" : "—"}</span>}
                    </td>
                    <td className="px-4 py-3 text-right whitespace-nowrap">
                      <div className="flex items-center justify-end gap-3">
                        {!i.analyzed && (
                          <button
                            onClick={() => runAnalyze(i.id)}
                            disabled={analyzing === i.id}
                            className="text-xs underline text-[#7B56C9] hover:text-[#2A0F5A] disabled:opacity-50"
                            data-testid={`analyze-row-${i.id}`}
                          >
                            {analyzing === i.id ? "Analysing…" : "Analyse"}
                          </button>
                        )}
                        <button
                          onClick={() => draftFIR(i.id)}
                          disabled={drafting === i.id || !i.analyzed}
                          className="text-xs underline text-[#2A0F5A] hover:text-[#7B56C9] disabled:opacity-30"
                          data-testid={`draft-fir-row-${i.id}`}
                        >
                          {drafting === i.id ? "Drafting…" : "Draft FIR"}
                        </button>
                        {i.case_id ? (
                          <Link to={`/app/cases/${i.case_id}`} className="text-xs underline text-[#2A0F5A]">Open case</Link>
                        ) : (
                          <button onClick={() => createCase(i.id)} className="text-xs underline text-[#2A0F5A] hover:text-[#7B56C9]" data-testid={`create-case-row-${i.id}`}>Create case</button>
                        )}
                        <button onClick={() => deleteIncident(i.id)} className="text-[#4A5568] hover:text-red-700" title="Delete" data-testid={`delete-incident-${i.id}`}>
                          <Trash2 size={14} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

function Field({ label, value, children }) {
  return (
    <div>
      <div className="label-eyebrow mb-0.5">{label}</div>
      <div className="text-sm">{children || value || <span className="italic text-[#4A5568]">—</span>}</div>
    </div>
  );
}
